import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import ScreenshotUpload from '../../components/ScreenshotUpload';
import {
  OcrMatchWarnings,
  StatsScoreCheck,
  collectOcrMatchIssues,
  hasOcrMatchIssues,
  sumStatPoints,
  scoreMismatches
} from '../../components/OcrMatchWarnings';
import { useTranslation } from 'react-i18next';

const GamesManagement = () => {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterPlayed, setFilterPlayed] = useState('all');
  // Nachrücker: Team additiv zum Spielplan hinzufügen
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [addTeamCandidates, setAddTeamCandidates] = useState([]);
  const [addTeamId, setAddTeamId] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [scoreData, setScoreData] = useState({ homeScore: '', awayScore: '', notes: '', gameDate: new Date().toISOString().split('T')[0] });
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [homeStats, setHomeStats] = useState({});
  const [awayStats, setAwayStats] = useState({});
  
  // OCR upload state
  const [entryMethod, setEntryMethod] = useState('manual'); // 'manual' or 'ocr'
  const [ocrData, setOcrData] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]); // For OCR player matching
  const [ocrExtracted, setOcrExtracted] = useState(false); // Track if OCR was used
  const [ocrMatchIssues, setOcrMatchIssues] = useState(null); // Namenszuordnung: was der OCR-Abgleich nicht sauber aufloesen konnte
  const [originalHomePlayers, setOriginalHomePlayers] = useState([]); // Store full roster
  const [originalAwayPlayers, setOriginalAwayPlayers] = useState([]); // Store full roster
  const [showBenched, setShowBenched] = useState(false); // Vom GM deaktivierte Spieler einblenden

  // Playoff tab state
  const [activeTab, setActiveTab] = useState('regular'); // 'regular' | 'playoffs'
  const [playoffSeries, setPlayoffSeries] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [isPlayoffSubmit, setIsPlayoffSubmit] = useState(false); // true when submitting a playoff game
  const [selectedSeries, setSelectedSeries] = useState(null); // the playoff series being submitted

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Auto-filter to GM's team when user data becomes available
  useEffect(() => {
    if (!isAdmin() && user?.teamId) {
      setFilterTeam(user.teamId.toString());
    }
  }, [user]);

  useEffect(() => {
    if (selectedSeason) {
      fetchGames();
    }
  }, [selectedSeason, filterTeam, filterPlayed]);

  const fetchInitialData = async () => {
    try {
      const [seasonsRes, teamsRes] = await Promise.all([
        api.get('/seasons'),
        api.get('/teams')
      ]);

      setSeasons(seasonsRes.data);
      setTeams(teamsRes.data);

      // Set active season as default
      const active = seasonsRes.data.find(s => s.isActive);
      if (active) {
        setSelectedSeason(active.id);
        setActiveSeason(active);
        if (active.playoffsStarted) {
          fetchPlayoffSeries(active.id);
        }
      } else if (seasonsRes.data.length > 0) {
        setSelectedSeason(seasonsRes.data[0].id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setLoading(false);
    }
  };

  const fetchPlayoffSeries = async (seasonId) => {
    try {
      const res = await api.get(`/playoffs/bracket/${seasonId}`);
      // Custom format: { format, config, rounds: { 'Sweet 16': [...], ... } }
      // Standard format: { 'First Round': { Eastern: [], Western: [] }, ... }
      let allSeries;
      if (res.data.format === 'custom') {
        allSeries = Object.values(res.data.rounds || {}).flat();
      } else {
        allSeries = Object.values(res.data).flatMap(v =>
          Array.isArray(v) ? v : Object.values(v).flat()
        );
      }
      // Keep only actual series objects (have higherSeedId)
      allSeries = allSeries.filter(s => s && s.higherSeedId);

      if (isAdmin()) {
        setPlayoffSeries(allSeries);
      } else {
        const teamId = Number(user?.teamId);
        setPlayoffSeries(allSeries.filter(s =>
          Number(s.higherSeedId) === teamId || Number(s.lowerSeedId) === teamId
        ));
      }
    } catch (error) {
      console.error('Error fetching playoff series:', error);
    }
  };

  const fetchGames = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSeason) params.append('seasonId', selectedSeason);
      if (filterTeam !== 'all') params.append('teamId', filterTeam);
      if (filterPlayed !== 'all') params.append('played', filterPlayed);

      const response = await api.get(`/games?${params.toString()}`);
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const handleGenerateSchedule = async () => {
    if (!selectedSeason) {
      alert('Please select a season first');
      return;
    }

    if (!window.confirm('Generate schedule for this season? This will delete any existing games.')) {
      return;
    }

    const startDate = prompt('Enter start date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!startDate) return;

    const postSchedule = async (confirmReplacePlayed) => {
      return api.post('/games/generate-schedule', {
        seasonId: selectedSeason,
        startDate,
        ...(confirmReplacePlayed ? { confirmReplacePlayed: true } : {})
      });
    };

    try {
      const response = await postSchedule(false);
      alert(response.data.message);
      fetchGames();
    } catch (error) {
      // Backend blocks regeneration when played games exist — confirm and retry.
      if (error.response?.status === 409 && error.response?.data?.code === 'SCHEDULE_HAS_PLAYED_GAMES') {
        const played = error.response.data.playedCount;
        if (!window.confirm(`This season already has ${played} played game(s). Regenerating will DELETE all games including their results. Continue?`)) {
          return;
        }
        try {
          const retry = await postSchedule(true);
          alert(retry.data.message);
          fetchGames();
        } catch (retryError) {
          alert(retryError.response?.data?.error || 'Error generating schedule');
        }
        return;
      }
      alert(error.response?.data?.error || 'Error generating schedule');
    }
  };

  const openAddTeam = async () => {
    if (!selectedSeason) {
      alert('Please select a season first');
      return;
    }
    try {
      const res = await api.get(`/games/schedule-status?seasonId=${selectedSeason}`);
      const missing = res.data.missingTeams || [];
      if (res.data.totalGames === 0) {
        alert('No schedule exists yet for this season. Generate the full schedule first.');
        return;
      }
      if (missing.length === 0) {
        alert('All teams with a GM are already in the schedule — no late-joiner to add.');
        return;
      }
      setAddTeamCandidates(missing);
      setAddTeamId(String(missing[0].id));
      setShowAddTeam(true);
    } catch (error) {
      alert(error.response?.data?.error || 'Error loading schedule status');
    }
  };

  const handleAddTeamToSchedule = async () => {
    if (!addTeamId) return;
    const team = addTeamCandidates.find((c) => String(c.id) === String(addTeamId));
    const label = team ? (team.abbreviation || team.name) : `Team ${addTeamId}`;
    if (!window.confirm(`Add ${label} to the schedule? This creates home & away games vs all current teams and appends them — existing games are kept.`)) {
      return;
    }
    setAddingTeam(true);
    try {
      const res = await api.post('/games/add-team-to-schedule', {
        seasonId: selectedSeason,
        teamId: parseInt(addTeamId, 10)
      });
      alert(res.data.message || `Added ${res.data.created} games.`);
      setShowAddTeam(false);
      setAddTeamId('');
      fetchGames();
    } catch (error) {
      alert(error.response?.data?.error || 'Error adding team to schedule');
    } finally {
      setAddingTeam(false);
    }
  };

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    
    if (!selectedGame) return;

    try {
      await api.put(`/games/${selectedGame.id}/submit-score`, {
        homeScore: parseInt(scoreData.homeScore),
        awayScore: parseInt(scoreData.awayScore),
        notes: scoreData.notes,
        gameDate: scoreData.gameDate
      });
      
      setShowScoreModal(false);
      setSelectedGame(null);
      setScoreData({ homeScore: '', awayScore: '', notes: '', gameDate: new Date().toISOString().split('T')[0] });
      fetchGames();
      alert('Score submitted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error submitting score');
    }
  };

  const openScoreModal = (game) => {
    setSelectedGame(game);
    setScoreData({
      homeScore: game.homeScore || '',
      awayScore: game.awayScore || '',
      notes: game.notes || '',
      gameDate: game.gameDate ? new Date(game.gameDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setShowScoreModal(true);
  };

  const openStatsModal = async (game) => {
    setSelectedGame(game);
    setScoreData({
      homeScore: game.homeScore || '',
      awayScore: game.awayScore || '',
      notes: game.notes || '',
      gameDate: game.gameDate ? new Date(game.gameDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });

    try {
      // Load players for both teams, all players for OCR matching, and existing stats if game was played.
      // includeBenched: inaktive Spieler werden hier gebraucht — standardmäßig
      // ausgeblendet, per Schalter einblendbar, und der OCR-Abgleich läuft gegen alle.
      const requests = [
        api.get(`/players?teamId=${game.homeTeamId}&includeBenched=true`),
        api.get(`/players?teamId=${game.awayTeamId}&includeBenched=true`),
        api.get('/players?includeBenched=true') // Get all players for OCR matching
      ];

      // If game was already played, fetch existing stats
      if (game.played) {
        requests.push(api.get(`/stats/game/${game.id}`));
      }

      const responses = await Promise.all(requests);
      const homePlayersRes = responses[0];
      const awayPlayersRes = responses[1];
      const allPlayersRes = responses[2];
      const existingStatsRes = responses[3]; // Will be undefined if game not played

      // Filter only active players for stats entry
      const activeHomePlayers = homePlayersRes.data.filter(p => p.isActive !== false);
      const activeAwayPlayers = awayPlayersRes.data.filter(p => p.isActive !== false);

      // Filter all players to only include active ones for OCR matching
      const activeAllPlayers = allPlayersRes.data.filter(p => p.isActive !== false);

      setHomePlayers(activeHomePlayers);
      setAwayPlayers(activeAwayPlayers);
      setAllPlayers(activeAllPlayers);
      
      // Store original full rosters (active players only) for manual entry mode
      setOriginalHomePlayers(activeHomePlayers);
      setOriginalAwayPlayers(activeAwayPlayers);

      // Initialize stats
      const homeStatsInit = {};
      const awayStatsInit = {};

      // First, create empty stats for all active players
      activeHomePlayers.forEach(p => {
        homeStatsInit[p.id] = createEmptyStats(p.id, game.homeTeamId);
      });

      activeAwayPlayers.forEach(p => {
        awayStatsInit[p.id] = createEmptyStats(p.id, game.awayTeamId);
      });

      // If game was played, overlay existing stats
      if (existingStatsRes && existingStatsRes.data) {
        existingStatsRes.data.forEach(stat => {
          if (stat.teamId === game.homeTeamId && homeStatsInit[stat.playerId]) {
            homeStatsInit[stat.playerId] = {
              playerId: stat.playerId,
              teamId: stat.teamId,
              minutes: stat.minutes || 0,
              points: stat.points || 0,
              rebounds: stat.rebounds || 0,
              assists: stat.assists || 0,
              steals: stat.steals || 0,
              blocks: stat.blocks || 0,
              turnovers: stat.turnovers || 0,
              fieldGoalsMade: stat.fieldGoalsMade || 0,
              fieldGoalsAttempted: stat.fieldGoalsAttempted || 0,
              threePointersMade: stat.threePointersMade || 0,
              threePointersAttempted: stat.threePointersAttempted || 0,
              freeThrowsMade: stat.freeThrowsMade || 0,
              freeThrowsAttempted: stat.freeThrowsAttempted || 0,
              offensiveRebounds: stat.offensiveRebounds || 0,
              fouls: stat.fouls || 0
            };
          } else if (stat.teamId === game.awayTeamId && awayStatsInit[stat.playerId]) {
            awayStatsInit[stat.playerId] = {
              playerId: stat.playerId,
              teamId: stat.teamId,
              minutes: stat.minutes || 0,
              points: stat.points || 0,
              rebounds: stat.rebounds || 0,
              assists: stat.assists || 0,
              steals: stat.steals || 0,
              blocks: stat.blocks || 0,
              turnovers: stat.turnovers || 0,
              fieldGoalsMade: stat.fieldGoalsMade || 0,
              fieldGoalsAttempted: stat.fieldGoalsAttempted || 0,
              threePointersMade: stat.threePointersMade || 0,
              threePointersAttempted: stat.threePointersAttempted || 0,
              freeThrowsMade: stat.freeThrowsMade || 0,
              freeThrowsAttempted: stat.freeThrowsAttempted || 0,
              offensiveRebounds: stat.offensiveRebounds || 0,
              fouls: stat.fouls || 0
            };
          }
        });
      }

      setHomeStats(homeStatsInit);
      setAwayStats(awayStatsInit);
      setShowBenched(false);
      setShowStatsModal(true);
    } catch (error) {
      alert('Error loading players: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOcrExtractComplete = (data) => {
    setOcrData(data);
    setOcrExtracted(true); // Mark that OCR was used

    const issues = [
      collectOcrMatchIssues({
        ocrPlayers: data.home.players,
        roster: originalHomePlayers,
        teamName: selectedGame.homeTeam?.name,
        allPlayers,
        teams
      }),
      collectOcrMatchIssues({
        ocrPlayers: data.away.players,
        roster: originalAwayPlayers,
        teamName: selectedGame.awayTeam?.name,
        allPlayers,
        teams
      })
    ];
    setOcrMatchIssues(hasOcrMatchIssues(issues) ? issues : null);

    // Convert OCR data to stats format
    const homeStatsUpdated = {};
    const awayStatsUpdated = {};

    // Nur Treffer uebernehmen, die auch im Kader der jeweiligen Mannschaft stehen.
    // Ein kaderfremder Treffer waere in der Tabelle unten unsichtbar, ginge beim
    // Absenden aber trotzdem mit — der Hinweiskasten listet ihn stattdessen auf.
    const homeRosterIds = new Set(originalHomePlayers.map(p => p.id));
    const awayRosterIds = new Set(originalAwayPlayers.map(p => p.id));

    const extractedHomePlayerIds = data.home.players
      .filter(p => p.playerId && homeRosterIds.has(p.playerId))
      .map(p => p.playerId);

    const extractedAwayPlayerIds = data.away.players
      .filter(p => p.playerId && awayRosterIds.has(p.playerId))
      .map(p => p.playerId);

    // Update homePlayers to only show extracted players
    setHomePlayers(prev => prev.filter(p => extractedHomePlayerIds.includes(p.id)));
    
    // Update awayPlayers to only show extracted players
    setAwayPlayers(prev => prev.filter(p => extractedAwayPlayerIds.includes(p.id)));
    
    // Process home team
    data.home.players.forEach(ocrPlayer => {
      if (ocrPlayer.playerId && homeRosterIds.has(ocrPlayer.playerId)) {
        homeStatsUpdated[ocrPlayer.playerId] = {
          playerId: ocrPlayer.playerId,
          teamId: selectedGame.homeTeamId,
          ...ocrPlayer.stats
        };
      }
    });
    
    // Process away team
    data.away.players.forEach(ocrPlayer => {
      if (ocrPlayer.playerId && awayRosterIds.has(ocrPlayer.playerId)) {
        awayStatsUpdated[ocrPlayer.playerId] = {
          playerId: ocrPlayer.playerId,
          teamId: selectedGame.awayTeamId,
          ...ocrPlayer.stats
        };
      }
    });
    
    setHomeStats(homeStatsUpdated);
    setAwayStats(awayStatsUpdated);
    
    // Auto-switch to review mode
    setEntryMethod('review');
  };

  // Vom GM deaktivierte Spieler sind standardmäßig ausgeblendet — außer sie haben schon Minuten
  const visiblePlayers = (players, stats) => (showBenched
    ? players
    : players.filter(p => !p.isBenched || stats[p.id]?.minutes > 0));

  const handleEntryMethodChange = (method) => {
    setEntryMethod(method);
    
    // If switching to manual mode after OCR extraction, restore full player lists
    if (method === 'manual' && ocrExtracted) {
      setHomePlayers(originalHomePlayers);
      setAwayPlayers(originalAwayPlayers);
      
      // Re-initialize stats for all players
      const homeStatsInit = {};
      const awayStatsInit = {};

      originalHomePlayers.forEach(p => {
        homeStatsInit[p.id] = homeStats[p.id] || createEmptyStats(p.id, selectedGame.homeTeamId);
      });

      originalAwayPlayers.forEach(p => {
        awayStatsInit[p.id] = awayStats[p.id] || createEmptyStats(p.id, selectedGame.awayTeamId);
      });

      setHomeStats(homeStatsInit);
      setAwayStats(awayStatsInit);
    }
  };

  const addMissingPlayer = (playerId, isHome) => {
    const player = (isHome ? originalHomePlayers : originalAwayPlayers).find(p => p.id === parseInt(playerId));
    if (!player) return;

    // Add player to the list
    if (isHome) {
      setHomePlayers(prev => [...prev, player]);
      setHomeStats(prev => ({
        ...prev,
        [player.id]: createEmptyStats(player.id, selectedGame.homeTeamId)
      }));
    } else {
      setAwayPlayers(prev => [...prev, player]);
      setAwayStats(prev => ({
        ...prev,
        [player.id]: createEmptyStats(player.id, selectedGame.awayTeamId)
      }));
    }
  };

  const removePlayer = (playerId, isHome) => {
    if (isHome) {
      setHomePlayers(prev => prev.filter(p => p.id !== playerId));
      setHomeStats(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    } else {
      setAwayPlayers(prev => prev.filter(p => p.id !== playerId));
      setAwayStats(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    }
  };

  const createEmptyStats = (playerId, teamId) => ({
    playerId,
    teamId,
    minutes: 0,
    points: 0,
    rebounds: 0,
    assists: 0,
    steals: 0,
    blocks: 0,
    turnovers: 0,
    fieldGoalsMade: 0,
    fieldGoalsAttempted: 0,
    threePointersMade: 0,
    threePointersAttempted: 0,
    freeThrowsMade: 0,
    freeThrowsAttempted: 0,
    offensiveRebounds: 0,
    fouls: 0
  });

  const updateStat = (playerId, isHome, stat, value) => {
    const stats = isHome ? homeStats : awayStats;
    const setStats = isHome ? setHomeStats : setAwayStats;

    setStats({
      ...stats,
      [playerId]: {
        ...stats[playerId],
        [stat]: parseInt(value) || 0
      }
    });
  };

  /**
   * Punktprobe vor dem Absenden. Der rote Kasten in der Tabelle ist nur ein
   * Hinweis — beim Spiel 2223 wurden so zwei Lakers-Zeilen uebersehen und eine
   * unvollstaendige Statistik gespeichert. Hier muss der Erfasser bestaetigen.
   * @param {Array<{name: string, sum: number, score: number}>} rows
   * @returns {boolean} true, wenn abgesendet werden darf
   */
  const confirmScoreMismatch = (rows) => {
    const mismatches = scoreMismatches(rows);
    if (mismatches.length === 0) return true;

    // Gar keine Statzeile: fast immer ein Versehen. Das Absenden loescht auf
    // dem Server erst alle vorhandenen Statistiken des Spiels — hier ist die
    // Warnung deutlicher als die blosse Punktdifferenz.
    if (rows.every(r => r.sum === 0)) {
      return window.confirm(t('ocrReview.confirmNoStats'));
    }

    const details = mismatches
      .map(r => t('ocrReview.confirmMismatchLine', {
        team: r.name, sum: r.sum, score: r.score, diff: r.score - r.sum
      }))
      .join('\n');

    return window.confirm(t('ocrReview.confirmMismatch', { details }));
  };

  const handleSubmitWithStats = async (e) => {
    e.preventDefault();

    if (!selectedGame) return;

    const homeScore = parseInt(scoreData.homeScore);
    const awayScore = parseInt(scoreData.awayScore);

    if (isNaN(homeScore) || isNaN(awayScore)) {
      alert('Please enter valid scores');
      return;
    }

    // Playoff submission path
    if (isPlayoffSubmit && selectedSeries) {
      // Client-side winner check for team owners
      if (!isAdmin()) {
        const teamId = Number(user?.teamId);
        const isHigherSeed = teamId === Number(selectedSeries.higherSeedId);
        const won = isHigherSeed ? homeScore > awayScore : awayScore > homeScore;
        if (!won) {
          alert('Only the winning team can submit the game result. Check your scores.');
          return;
        }
      }

      if (!confirmScoreMismatch([
        { name: selectedSeries.higherSeed?.name, sum: sumStatPoints(homeStats), score: homeScore },
        { name: selectedSeries.lowerSeed?.name, sum: sumStatPoints(awayStats), score: awayScore }
      ])) return;

      try {
        const allStats = [
          ...Object.values(homeStats).filter(s => s.minutes > 0),
          ...Object.values(awayStats).filter(s => s.minutes > 0)
        ];

        const res = await api.post(`/playoffs/series/${selectedSeries.id}/submit-game`, {
          higherSeedScore: homeScore,
          lowerSeedScore: awayScore,
          stats: allStats
        });

        alert(res.data.message);
        resetStatsModal();
        fetchPlayoffSeries(activeSeason.id);
      } catch (error) {
        alert(error.response?.data?.error || 'Error submitting playoff game');
      }
      return;
    }

    if (!confirmScoreMismatch([
      { name: selectedGame.homeTeam?.name, sum: sumStatPoints(homeStats), score: homeScore },
      { name: selectedGame.awayTeam?.name, sum: sumStatPoints(awayStats), score: awayScore }
    ])) return;

    try {
      // Combine all stats (only include players who played - minutes > 0)
      const allStats = [
        ...Object.values(homeStats).filter(s => s.minutes > 0),
        ...Object.values(awayStats).filter(s => s.minutes > 0)
      ];

      await api.put(`/games/${selectedGame.id}/submit-with-stats`, {
        homeScore,
        awayScore,
        notes: scoreData.notes,
        gameDate: scoreData.gameDate,
        stats: allStats
      });

      resetStatsModal();
      fetchGames();
      alert('Score and stats submitted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error submitting score and stats');
    }
  };

  const resetStatsModal = () => {
    setShowStatsModal(false);
    setSelectedGame(null);
    setSelectedSeries(null);
    setIsPlayoffSubmit(false);
    setScoreData({ homeScore: '', awayScore: '', notes: '', gameDate: new Date().toISOString().split('T')[0] });
    setHomePlayers([]);
    setAwayPlayers([]);
    setHomeStats({});
    setAwayStats({});
    setEntryMethod('manual');
    setOcrData(null);
    setOcrExtracted(false);
    setOcrMatchIssues(null);
  };

  const openPlayoffStatsModal = async (series) => {
    setSelectedSeries(series);
    setIsPlayoffSubmit(true);
    setScoreData({ homeScore: '', awayScore: '', notes: '', gameDate: new Date().toISOString().split('T')[0] });

    // Map series to the "game" shape the existing modal expects
    // higherSeed = home, lowerSeed = away
    setSelectedGame({
      id: null,
      homeTeamId: series.higherSeedId,
      awayTeamId: series.lowerSeedId,
      homeTeam: series.higherSeed,
      awayTeam: series.lowerSeed,
      played: false
    });

    try {
      const [homeRes, awayRes, allRes] = await Promise.all([
        api.get(`/players?teamId=${series.higherSeedId}&includeBenched=true`),
        api.get(`/players?teamId=${series.lowerSeedId}&includeBenched=true`),
        api.get('/players?includeBenched=true')
      ]);

      const activeHome = homeRes.data.filter(p => p.isActive !== false);
      const activeAway = awayRes.data.filter(p => p.isActive !== false);
      const activeAll = allRes.data.filter(p => p.isActive !== false);

      setHomePlayers(activeHome);
      setAwayPlayers(activeAway);
      setAllPlayers(activeAll);
      setOriginalHomePlayers(activeHome);
      setOriginalAwayPlayers(activeAway);

      const homeStatsInit = {};
      const awayStatsInit = {};
      activeHome.forEach(p => { homeStatsInit[p.id] = createEmptyStats(p.id, series.higherSeedId); });
      activeAway.forEach(p => { awayStatsInit[p.id] = createEmptyStats(p.id, series.lowerSeedId); });
      setHomeStats(homeStatsInit);
      setAwayStats(awayStatsInit);

      setShowBenched(false);
      setShowStatsModal(true);
    } catch (error) {
      alert('Error loading players: ' + (error.response?.data?.error || error.message));
      setSelectedGame(null);
      setSelectedSeries(null);
      setIsPlayoffSubmit(false);
    }
  };

  const canEditGame = (game) => {
    if (isAdmin()) return true;
    const teamId = Number(user.teamId);
    if (teamId === Number(game.homeTeamId) || teamId === Number(game.awayTeamId)) return true;
    return false;
  };

  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    try {
      await api.delete(`/games/${gameId}`);
      fetchGames();
    } catch (error) {
      alert(error.response?.data?.error || 'Error deleting game');
    }
  };

  const handleResetGame = async (gameId) => {
    if (!window.confirm('Reset this game? Score and all player stats will be removed, but the game will remain in the schedule.')) return;

    try {
      const res = await api.put(`/games/${gameId}/reset`);
      fetchGames();
      alert(`Game reset successfully. ${res.data.statsRemoved} stat records removed.`);
    } catch (error) {
      alert(error.response?.data?.error || 'Error resetting game');
    }
  };

  // Freispiel-Modus: kein fester Wochenplan. Spiele nach Status gruppieren —
  // offene Spiele zuerst, dann bereits gespielte (nach Datum absteigend).
  const gameGroups = [
    {
      key: 'open',
      label: 'Offene Spiele',
      items: games.filter((g) => !g.played)
    },
    {
      key: 'played',
      label: 'Gespielte Spiele',
      items: games
        .filter((g) => g.played)
        .sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate))
    }
  ].filter((grp) => grp.items.length > 0);

  if (loading) {
    return <div className="text-center py-12">Loading games...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
          {isAdmin() ? 'Games Management' : 'My Games'}
        </h2>
        {isAdmin() && activeTab === 'regular' && (
          <div className="flex gap-2">
            <button
              onClick={openAddTeam}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              title="Add a late-joiner (Nachrücker) team to the existing schedule without deleting it"
            >
              ➕ Nachrücker hinzufügen
            </button>
            <button
              onClick={handleGenerateSchedule}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
            >
              🗓️ Generate Schedule
            </button>
          </div>
        )}
      </div>

      {/* Nachrücker: Team additiv zum Spielplan hinzufügen */}
      {isAdmin() && activeTab === 'regular' && showAddTeam && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nachrücker-Team (spielt Hin &amp; Rück gegen alle bestehenden Teams, angehängt)
              </label>
              <select
                value={addTeamId}
                onChange={(e) => setAddTeamId(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                {addTeamCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.city ? `${c.city} ${c.name}` : c.name} {c.abbreviation ? `(${c.abbreviation})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddTeamToSchedule}
                disabled={addingTeam || !addTeamId}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {addingTeam ? 'Wird hinzugefügt…' : 'Zum Spielplan hinzufügen'}
              </button>
              <button
                onClick={() => setShowAddTeam(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab switcher — only show Playoffs tab when playoffs have started */}
      {activeSeason?.playoffsStarted && (
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('regular')}
            className={`px-5 py-2 text-sm font-medium rounded-t-lg transition ${
              activeTab === 'regular'
                ? 'bg-white dark:bg-gray-800 border border-b-white dark:border-gray-600 text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            🏀 Regular Season
          </button>
          <button
            onClick={() => setActiveTab('playoffs')}
            className={`px-5 py-2 text-sm font-medium rounded-t-lg transition ${
              activeTab === 'playoffs'
                ? 'bg-white dark:bg-gray-800 border border-b-white dark:border-gray-600 text-yellow-600 dark:text-yellow-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            🏆 Playoffs
          </button>
        </div>
      )}

      {/* ── PLAYOFFS TAB ── */}
      {activeTab === 'playoffs' && (
        <div>
          {playoffSeries.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
              No active playoff series found for your team.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Group by round — use actual round names from data to support custom formats */}
              {[...new Set(playoffSeries.map(s => s.round))].map(round => {
                const roundSeries = playoffSeries.filter(s => s.round === round);
                if (roundSeries.length === 0) return null;
                return (
                  <div key={round}>
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">🏆 {round}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {roundSeries.map(series => {
                        const higherName = series.higherSeed?.name || `Team ${series.higherSeedId}`;
                        const lowerName = series.lowerSeed?.name || `Team ${series.lowerSeedId}`;
                        const userTeamId = Number(user?.teamId);
                        const userInSeries = isAdmin() ||
                          userTeamId === Number(series.higherSeedId) ||
                          userTeamId === Number(series.lowerSeedId);

                        // Determine series format wins needed
                        const fmt = series.seriesFormat || 'Best-of-7';
                        const winsNeeded = fmt === 'Best-of-1' ? 1 : fmt === 'Best-of-3' ? 2 : fmt === 'Best-of-5' ? 3 : 4;
                        const totalGames = (series.higherSeedWins || 0) + (series.lowerSeedWins || 0);

                        return (
                          <div key={series.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 ${series.isComplete ? 'border-gray-400' : 'border-yellow-500'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {fmt} · Game {totalGames + (series.isComplete ? 0 : 1)}
                              </span>
                              {series.isComplete && (
                                <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">Complete</span>
                              )}
                            </div>

                            {/* Matchup */}
                            <div className="flex items-center justify-between gap-2 mb-4">
                              <div className="flex-1 text-center">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{higherName}</div>
                                <div className={`text-3xl font-bold mt-1 ${series.winnerId === series.higherSeedId ? 'text-green-600' : 'text-gray-800 dark:text-gray-200'}`}>
                                  {series.higherSeedWins || 0}
                                </div>
                              </div>
                              <div className="text-gray-400 font-bold text-sm">–</div>
                              <div className="flex-1 text-center">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{lowerName}</div>
                                <div className={`text-3xl font-bold mt-1 ${series.winnerId === series.lowerSeedId ? 'text-green-600' : 'text-gray-800 dark:text-gray-200'}`}>
                                  {series.lowerSeedWins || 0}
                                </div>
                              </div>
                            </div>

                            <div className="text-xs text-center text-gray-400 dark:text-gray-500 mb-3">
                              First to {winsNeeded} wins
                            </div>

                            {/* Submit button */}
                            {!series.isComplete && userInSeries && (
                              <button
                                onClick={() => openPlayoffStatsModal(series)}
                                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg transition text-sm"
                              >
                                📋 Submit Game {totalGames + 1} Result
                              </button>
                            )}
                            {series.isComplete && series.winnerId && (
                              <div className="text-center text-sm font-medium text-green-600 dark:text-green-400">
                                🏆 {series.winnerId === series.higherSeedId ? higherName : lowerName} advances
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── REGULAR SEASON TAB ── (hidden when playoffs tab is active) */}
      {activeTab === 'regular' && (
      <div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Season</label>
            <select
              value={selectedSeason || ''}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">Select Season</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} {season.isActive && '(Active)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Team</label>
            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={!isAdmin() && !!user?.teamId}
            >
              {isAdmin() && <option value="all">All Teams</option>}
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={filterPlayed}
              onChange={(e) => setFilterPlayed(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="all">All Games</option>
              <option value="true">Played</option>
              <option value="false">Upcoming</option>
            </select>
          </div>
        </div>
      </div>

      {/* Games List */}
      {!selectedSeason ? (
        <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
          Please select a season to view games
        </div>
      ) : games.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-12 rounded-lg shadow text-center text-gray-500 dark:text-gray-400">
          No games found. {isAdmin() && 'Click "Generate Schedule" to create games.'}
        </div>
      ) : (
        <div className="space-y-6">
          {gameGroups.map((group) => (
            <div key={group.key} className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">
                  {group.label} ({group.items.length})
                </h3>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {group.items.map((game) => (
                  <div key={game.id} className="px-6 py-4 hover:bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          {/* Home Team */}
                          <div className="flex-1 text-right">
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {game.homeTeam?.name || 'Unknown'}
                            </span>
                            {game.played && (
                              <span className="ml-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                                {game.homeScore}
                              </span>
                            )}
                          </div>

                          {/* VS */}
                          <div className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-medium text-gray-600 dark:text-gray-400">
                            VS
                          </div>

                          {/* Away Team */}
                          <div className="flex-1 text-left">
                            {game.played && (
                              <span className="mr-2 text-2xl font-bold text-red-600 dark:text-red-400">
                                {game.awayScore}
                              </span>
                            )}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              {game.awayTeam?.name || 'Unknown'}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex gap-4">
                          {game.played && <span>📅 {new Date(game.gameDate).toLocaleDateString()}</span>}
                          {game.played && game.submitter && (
                            <span>👤 Submitted by {game.submitter.username}</span>
                          )}
                          {game.notes && <span>📝 {game.notes}</span>}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="ml-4 flex gap-2">
                        {!game.played && canEditGame(game) && (
                          <>
                            <button
                              onClick={() => openScoreModal(game)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                            >
                              Enter Score
                            </button>
                            <button
                              onClick={() => openStatsModal(game)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm"
                            >
                              📊 Enter Stats
                            </button>
                          </>
                        )}
                        {game.played && canEditGame(game) && (
                          <>
                            <button
                              onClick={() => openScoreModal(game)}
                              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm"
                            >
                              Edit Score
                            </button>
                            <button
                              onClick={() => openStatsModal(game)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm"
                            >
                              📊 Edit Stats
                            </button>
                          </>
                        )}
                        {isAdmin() && game.played && (
                          <button
                            onClick={() => handleResetGame(game.id)}
                            className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 transition text-sm"
                            title="Reset game (clear score & stats)"
                          >
                            🔄
                          </button>
                        )}
                        {isAdmin() && (
                          <button
                            onClick={() => handleDeleteGame(game.id)}
                            className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition text-sm"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Score Submit Modal */}
      {showScoreModal && selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">
              {selectedGame.played ? 'Edit Score' : 'Enter Score'}
            </h3>
            
            <form onSubmit={handleSubmitScore}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {selectedGame.homeTeam?.name} Score
                  </label>
                  <input
                    type="number"
                    value={scoreData.homeScore}
                    onChange={(e) => setScoreData({ ...scoreData, homeScore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {selectedGame.awayTeam?.name} Score
                  </label>
                  <input
                    type="number"
                    value={scoreData.awayScore}
                    onChange={(e) => setScoreData({ ...scoreData, awayScore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    📅 Date Played *
                  </label>
                  <input
                    type="date"
                    value={scoreData.gameDate}
                    onChange={(e) => setScoreData({ ...scoreData, gameDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={scoreData.notes}
                    onChange={(e) => setScoreData({ ...scoreData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    rows="3"
                    placeholder="Add any notes about this game..."
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                >
                  Submit Score
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowScoreModal(false);
                    setSelectedGame(null);
                    setScoreData({ homeScore: '', awayScore: '', notes: '', gameDate: new Date().toISOString().split('T')[0] });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      </div>
      )} {/* end activeTab === 'regular' */}

      {/* Stats Entry Modal — shared by both regular season and playoffs tabs */}
      {showStatsModal && selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-6xl my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-1">
              {isPlayoffSubmit ? '🏆 Submit Playoff Game Result' : 'Enter Game Stats'}
            </h3>
            {isPlayoffSubmit && selectedSeries && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-3">
                {selectedSeries.round} · Series: {selectedSeries.higherSeed?.name} {selectedSeries.higherSeedWins}–{selectedSeries.lowerSeedWins} {selectedSeries.lowerSeed?.name} · Only the winning team submits
              </p>
            )}

            <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold">{selectedGame.homeTeam?.name}{isPlayoffSubmit ? ' (Higher Seed)' : ''}</span>
                <span className="text-gray-500 dark:text-gray-400">vs</span>
                <span className="font-semibold">{selectedGame.awayTeam?.name}{isPlayoffSubmit ? ' (Lower Seed)' : ''}</span>
              </div>
            </div>

            <form onSubmit={handleSubmitWithStats}>
              {/* Score Input */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {selectedGame.homeTeam?.name} Score
                  </label>
                  <input
                    type="number"
                    value={scoreData.homeScore}
                    onChange={(e) => setScoreData({ ...scoreData, homeScore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                    required
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {selectedGame.awayTeam?.name} Score
                  </label>
                  <input
                    type="number"
                    value={scoreData.awayScore}
                    onChange={(e) => setScoreData({ ...scoreData, awayScore: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                    required
                    min="0"
                  />
                </div>
                {!isPlayoffSubmit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">📅 Date Played *</label>
                    <input
                      type="date"
                      value={scoreData.gameDate}
                      onChange={(e) => setScoreData({ ...scoreData, gameDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                      required
                    />
                  </div>
                )}
                {!isPlayoffSubmit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                    <input
                      type="text"
                      value={scoreData.notes}
                      onChange={(e) => setScoreData({ ...scoreData, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                      placeholder="Optional notes..."
                    />
                  </div>
                )}
              </div>

              {/* Entry Method Tabs */}
              <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => handleEntryMethodChange('manual')}
                    className={`px-4 py-2 font-medium rounded-t-lg ${
                      entryMethod === 'manual' || entryMethod === 'review'
                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    ✏️ Manual Entry
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEntryMethodChange('ocr')}
                    className={`px-4 py-2 font-medium rounded-t-lg ${
                      entryMethod === 'ocr'
                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    📸 Upload Screenshots
                  </button>
                </div>
              </div>

              {/* OCR Upload Section */}
              {entryMethod === 'ocr' && (
                <ScreenshotUpload
                  homeTeam={selectedGame.homeTeam}
                  awayTeam={selectedGame.awayTeam}
                  players={allPlayers}
                  homePlayers={originalHomePlayers}
                  awayPlayers={originalAwayPlayers}
                  onExtractComplete={handleOcrExtractComplete}
                />
              )}

              {/* Namensabgleich: was der OCR-Abgleich nicht sauber aufloesen konnte */}
              {(entryMethod === 'review' || (entryMethod === 'manual' && ocrExtracted)) && (
                <OcrMatchWarnings issues={ocrMatchIssues} />
              )}

              {/* Punktprobe: Summe der Statzeilen gegen den eingetragenen Endstand */}
              {(entryMethod === 'manual' || entryMethod === 'review') && (
                <StatsScoreCheck rows={[
                  { name: selectedGame.homeTeam?.name, sum: sumStatPoints(homeStats), score: parseInt(scoreData.homeScore, 10) },
                  { name: selectedGame.awayTeam?.name, sum: sumStatPoints(awayStats), score: parseInt(scoreData.awayScore, 10) }
                ]} />
              )}

              {/* Review Mode Banner */}
              {(entryMethod === 'review' || (entryMethod === 'manual' && ocrExtracted)) && ocrData && (
                <div className="p-4 bg-green-50 border-l-4 border-green-400 dark:border-green-700 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        Stats extracted successfully!
                      </h3>
                      <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                        <p>Review the extracted stats below and make any necessary corrections before submitting. Only players found in the screenshots are shown.</p>
                        {ocrData.warnings && (ocrData.warnings.home.length > 0 || ocrData.warnings.away.length > 0) && (
                          <div className="mt-2">
                            <p className="font-semibold">⚠️ Warnings:</p>
                            {ocrData.warnings.home.map((w, idx) => (
                              <p key={`home-${idx}`} className="text-xs">• {w.player.playerName}: {w.warnings.join(', ')}</p>
                            ))}
                            {ocrData.warnings.away.map((w, idx) => (
                              <p key={`away-${idx}`} className="text-xs">• {w.player.playerName}: {w.warnings.join(', ')}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(entryMethod === 'manual' || entryMethod === 'review')
                && [...originalHomePlayers, ...originalAwayPlayers].some(p => p.isBenched) && (
                <label className="flex items-center gap-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={showBenched}
                    onChange={(e) => setShowBenched(e.target.checked)}
                  />
                  {t('bench.showBenched')}
                  <span className="text-gray-500 dark:text-gray-400">
                    ({[...originalHomePlayers, ...originalAwayPlayers].filter(p => p.isBenched).length})
                  </span>
                </label>
              )}

              {/* Home Team Stats */}
              {(entryMethod === 'manual' || entryMethod === 'review') && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-blue-600 dark:text-blue-400">
                  {selectedGame.homeTeam?.name} Stats
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-2 py-2 text-left">Player</th>
                        <th className="px-2 py-2">MIN</th>
                        <th className="px-2 py-2">PTS</th>
                        <th className="px-2 py-2">REB</th>
                        <th className="px-2 py-2">AST</th>
                        <th className="px-2 py-2">STL</th>
                        <th className="px-2 py-2">BLK</th>
                        <th className="px-2 py-2">TO</th>
                        <th className="px-2 py-2">FG</th>
                        <th className="px-2 py-2">3PT</th>
                        <th className="px-2 py-2">FT</th>
                        <th className="px-2 py-2">OR</th>
                        <th className="px-2 py-2">FLS</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePlayers(homePlayers, homeStats).map(player => (
                        <tr key={player.id} className={`border-b ${player.isBenched ? 'opacity-60' : ''}`}>
                          <td className="px-2 py-2 font-medium">
                            {player.firstName} {player.lastName}
                            {player.isBenched && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({t('bench.benchedHint')})</span>}
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" max="48" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.minutes || 0}
                              onChange={(e) => updateStat(player.id, true, 'minutes', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.points || 0}
                              onChange={(e) => updateStat(player.id, true, 'points', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.rebounds || 0}
                              onChange={(e) => updateStat(player.id, true, 'rebounds', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.assists || 0}
                              onChange={(e) => updateStat(player.id, true, 'assists', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.steals || 0}
                              onChange={(e) => updateStat(player.id, true, 'steals', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.blocks || 0}
                              onChange={(e) => updateStat(player.id, true, 'blocks', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.turnovers || 0}
                              onChange={(e) => updateStat(player.id, true, 'turnovers', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={homeStats[player.id]?.fieldGoalsMade || 0}
                                onChange={(e) => updateStat(player.id, true, 'fieldGoalsMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={homeStats[player.id]?.fieldGoalsAttempted || 0}
                                onChange={(e) => updateStat(player.id, true, 'fieldGoalsAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={homeStats[player.id]?.threePointersMade || 0}
                                onChange={(e) => updateStat(player.id, true, 'threePointersMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={homeStats[player.id]?.threePointersAttempted || 0}
                                onChange={(e) => updateStat(player.id, true, 'threePointersAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={homeStats[player.id]?.freeThrowsMade || 0}
                                onChange={(e) => updateStat(player.id, true, 'freeThrowsMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={homeStats[player.id]?.freeThrowsAttempted || 0}
                                onChange={(e) => updateStat(player.id, true, 'freeThrowsAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.offensiveRebounds || 0}
                              onChange={(e) => updateStat(player.id, true, 'offensiveRebounds', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={homeStats[player.id]?.fouls || 0}
                              onChange={(e) => updateStat(player.id, true, 'fouls', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <button
                              type="button"
                              onClick={() => removePlayer(player.id, true)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 text-lg font-bold"
                              title={`Remove ${player.firstName} ${player.lastName}`}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add Missing Player for Home Team (only in review mode) */}
                {entryMethod === 'review' && ocrExtracted && (
                  <div className="mt-3">
                    {originalHomePlayers.filter(p => !homePlayers.find(hp => hp.id === p.id)).length > 0 ? (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Missing a player?</label>
                        <select
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                          onChange={(e) => {
                            if (e.target.value) {
                              addMissingPlayer(e.target.value, true);
                              e.target.value = '';
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">+ Add Player</option>
                          {originalHomePlayers
                            .filter(p => !homePlayers.find(hp => hp.id === p.id))
                            .map(player => (
                              <option key={player.id} value={player.id}>
                                {player.firstName} {player.lastName}
                              </option>
                            ))}
                        </select>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({originalHomePlayers.filter(p => !homePlayers.find(hp => hp.id === p.id)).length} not shown)
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-green-600 dark:text-green-400">✓ All players included</p>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Away Team Stats */}
              {(entryMethod === 'manual' || entryMethod === 'review') && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3 text-red-600 dark:text-red-400">
                  {selectedGame.awayTeam?.name} Stats
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-2 py-2 text-left">Player</th>
                        <th className="px-2 py-2">MIN</th>
                        <th className="px-2 py-2">PTS</th>
                        <th className="px-2 py-2">REB</th>
                        <th className="px-2 py-2">AST</th>
                        <th className="px-2 py-2">STL</th>
                        <th className="px-2 py-2">BLK</th>
                        <th className="px-2 py-2">TO</th>
                        <th className="px-2 py-2">FG</th>
                        <th className="px-2 py-2">3PT</th>
                        <th className="px-2 py-2">FT</th>
                        <th className="px-2 py-2">OR</th>
                        <th className="px-2 py-2">FLS</th>
                        <th className="px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePlayers(awayPlayers, awayStats).map(player => (
                        <tr key={player.id} className={`border-b ${player.isBenched ? 'opacity-60' : ''}`}>
                          <td className="px-2 py-2 font-medium">
                            {player.firstName} {player.lastName}
                            {player.isBenched && <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({t('bench.benchedHint')})</span>}
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" max="48" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.minutes || 0}
                              onChange={(e) => updateStat(player.id, false, 'minutes', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.points || 0}
                              onChange={(e) => updateStat(player.id, false, 'points', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.rebounds || 0}
                              onChange={(e) => updateStat(player.id, false, 'rebounds', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.assists || 0}
                              onChange={(e) => updateStat(player.id, false, 'assists', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.steals || 0}
                              onChange={(e) => updateStat(player.id, false, 'steals', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.blocks || 0}
                              onChange={(e) => updateStat(player.id, false, 'blocks', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.turnovers || 0}
                              onChange={(e) => updateStat(player.id, false, 'turnovers', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={awayStats[player.id]?.fieldGoalsMade || 0}
                                onChange={(e) => updateStat(player.id, false, 'fieldGoalsMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={awayStats[player.id]?.fieldGoalsAttempted || 0}
                                onChange={(e) => updateStat(player.id, false, 'fieldGoalsAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={awayStats[player.id]?.threePointersMade || 0}
                                onChange={(e) => updateStat(player.id, false, 'threePointersMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={awayStats[player.id]?.threePointersAttempted || 0}
                                onChange={(e) => updateStat(player.id, false, 'threePointersAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <div className="flex gap-1">
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="M"
                                value={awayStats[player.id]?.freeThrowsMade || 0}
                                onChange={(e) => updateStat(player.id, false, 'freeThrowsMade', e.target.value)} />
                              <span>/</span>
                              <input type="number" min="0" className="w-12 px-1 py-1 border rounded text-center" placeholder="A"
                                value={awayStats[player.id]?.freeThrowsAttempted || 0}
                                onChange={(e) => updateStat(player.id, false, 'freeThrowsAttempted', e.target.value)} />
                            </div>
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.offensiveRebounds || 0}
                              onChange={(e) => updateStat(player.id, false, 'offensiveRebounds', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <input type="number" min="0" className="w-16 px-1 py-1 border rounded text-center"
                              value={awayStats[player.id]?.fouls || 0}
                              onChange={(e) => updateStat(player.id, false, 'fouls', e.target.value)} />
                          </td>
                          <td className="px-1 py-2">
                            <button
                              type="button"
                              onClick={() => removePlayer(player.id, false)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 text-lg font-bold"
                              title={`Remove ${player.firstName} ${player.lastName}`}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add Missing Player for Away Team (only in review mode) */}
                {entryMethod === 'review' && ocrExtracted && (
                  <div className="mt-3">
                    {originalAwayPlayers.filter(p => !awayPlayers.find(ap => ap.id === p.id)).length > 0 ? (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Missing a player?</label>
                        <select
                          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                          onChange={(e) => {
                            if (e.target.value) {
                              addMissingPlayer(e.target.value, false);
                              e.target.value = '';
                            }
                          }}
                          defaultValue=""
                        >
                          <option value="">+ Add Player</option>
                          {originalAwayPlayers
                            .filter(p => !awayPlayers.find(ap => ap.id === p.id))
                            .map(player => (
                              <option key={player.id} value={player.id}>
                                {player.firstName} {player.lastName}
                              </option>
                            ))}
                        </select>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({originalAwayPlayers.filter(p => !awayPlayers.find(ap => ap.id === p.id)).length} not shown)
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-green-600 dark:text-green-400">✓ All players included</p>
                    )}
                  </div>
                )}
              </div>
              )}

              <div className="flex gap-2 mt-6 sticky bottom-0 bg-white dark:bg-gray-800 pt-4 border-t">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
                >
                  💾 Submit Score & Stats
                </button>
                <button
                  type="button"
                  onClick={resetStatsModal}
                  className="flex-1 bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-lg hover:bg-gray-400 transition font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default GamesManagement;
